import { FiChevronLeft, FiCreditCard } from 'react-icons/fi';

import NoYachtFallback from '@/assets/no-yacht-fallback-new.svg';
import { Avatar } from '@/components';
import {
  BackButton,
  DetailHeader,
  EmptyState,
  Group,
  GroupLabel,
  Row,
} from '@/design-system/Layout';

import { isLifetimePlan, isTrialing, planSummary } from '../subscription.display';
import s from '../subscription.module.scss';
import type { ManagedVesselSubscription } from '../useManagedVesselSubscriptions';

import { StatusChip } from './Chips';
import { PortalButton } from './PortalButton';
import { UserBadge } from './UserBadge';

// Back to the main Vessel Haven app's settings (external — domain from env).
const HAVEN_SETTINGS_URL = `${import.meta.env.VITE_APP_WEB_URL}/settings?tab=subscriptions`;

type VesselListProps = {
  items: ManagedVesselSubscription[];
  isLoading: boolean;
  /** Vessels render first; the plan line fills in when this clears. */
  isSubscriptionsPending: boolean;
  error: unknown;
  /** Drills into the vessel's detail view, where its plan is changed. */
  onOpen: (item: ManagedVesselSubscription) => void;
};

/**
 * Every vessel whose plan the user may manage, one row each. The rows only report which plan
 * a vessel is on, plus a chip when that plan is still a trial — changing the plan lives in
 * the detail view behind the row.
 */
export function VesselList({
  items,
  isLoading,
  isSubscriptionsPending,
  error,
  onOpen,
}: VesselListProps) {
  const renderRows = () => {
    if (isLoading) return <EmptyState>Loading your vessels…</EmptyState>;
    if (error) return <EmptyState>Could not load your vessels. Please try again.</EmptyState>;
    if (items.length === 0) return <EmptyState>No vessels to manage yet.</EmptyState>;

    return items.map((item) => (
      <Row
        key={item.vessel.id}
        leading={
          <Avatar
            src={item.vessel.image}
            fallbackSrc={NoYachtFallback}
            serviceId={item.vessel.id}
            firstName={item.vessel.name}
            size={38}
          />
        }
        title={item.vessel.name}
        sub={isSubscriptionsPending ? 'Loading plan…' : planSummary(item.subscription)}
        // Same chip the detail header carries, and only for a trial or a lifetime plan.
        trailing={
          isTrialing(item.subscription) ? (
            <StatusChip status="trial" />
          ) : isLifetimePlan(item.subscription) ? (
            <StatusChip status="lifetime" />
          ) : undefined
        }
        chevron
        onClick={() => onOpen(item)}
        label={`Open ${item.vessel.name}`}
      />
    ));
  };

  return (
    <>
      {/* The current user sits on the back-link line, opposite it. */}
      <div className={s.topBar}>
        <BackButton href={HAVEN_SETTINGS_URL}>
          <FiChevronLeft /> Vessel Haven
        </BackButton>
        <UserBadge />
      </div>

      <DetailHeader
        icon={<FiCreditCard />}
        title="Manage your account"
        sub="Plans & crew seats across your vessels"
        trailing={<PortalButton />}
      />

      <GroupLabel>Your vessels{items.length > 0 && ` · ${items.length}`}</GroupLabel>
      <Group>{renderRows()}</Group>
    </>
  );
}
